import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { getMyLocationInfo } from 'utils/getMyLocationInfo';
import { arabianCountries } from 'utils';
import { changeLanguage } from 'i18next';
import { useRouter } from 'next/router';

type IntlProviderProps = {
  children: React.ReactNode;
};

type ContextProps = {
  currentCurrency: string;
  handleCurrentCurrency: (x: string) => void;
  show: boolean;
};

export const IntlContext = createContext<ContextProps>({
  currentCurrency: 'USD',
  handleCurrentCurrency: () => {},
  show: false,
});

export function useIntlContext() {
  const alertContext = useContext(IntlContext);

  if (!alertContext) {
    throw new Error('useAlertContext must be used within a AlertProvider');
  }
  return alertContext;
}

export const IntlProvider = ({ children }: IntlProviderProps) => {
  const { userCountry } = getMyLocationInfo();
  const isArabianCountry = arabianCountries.includes(userCountry);
  const [currentCurrency, setCurrentCurrency] = useState('USD');
  const { ready } = useTranslation();
  const [show, setShow] = useState(false);
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const currency = localStorage.getItem('webmarket-currency');
      if (currency) {
        setCurrentCurrency(currency);
      } else if (isArabianCountry) {
        setCurrentCurrency('AED');
      } else {
        setCurrentCurrency('USD');
      }
    }
  }, []);

  useEffect(() => {
    if (ready) {
      setShow(true);
    }
  }, [ready]);

  useEffect(() => {
    document.documentElement.dir = i18n.dir();
    document.documentElement.lang = i18n.language;
  }, [currentLanguage, i18n, t]);

  useEffect(() => {
    changeLanguage(router.locale);
  }, [router.locale]);

  useEffect(() => {
    localStorage.setItem('webmarket-currency', currentCurrency);
  }, [currentCurrency]);

  const handleCurrentCurrency = (str: string) => {
    setCurrentCurrency(str);
  };

  const value = {
    currentCurrency,
    handleCurrentCurrency,
    show,
  };

  const data = ['/', '/product/[id]'];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isSsr = data.includes(router.pathname);

  return <IntlContext.Provider value={value}>{children}</IntlContext.Provider>;
};
